/**
 * Stale-while-revalidate cache for API responses.
 * Shows cached data instantly, refreshes in background.
 *
 * Security note: we use `sessionStorage` (not localStorage) so values are
 * scoped to the browser tab and discarded automatically when the tab closes.
 * Auth tokens are NOT stored here — only response payloads (contacts, groups)
 * which are also fetched server-side under the same auth cookie. Cleared
 * explicitly on logout via `clearAllCache()`.
 */

const CACHE_PREFIX = 'pik_cache_';
const DEFAULT_TTL = 120_000; // 2 minutes

function getKey(key) {
  return CACHE_PREFIX + key;
}

export function getCached(key) {
  try {
    const raw = sessionStorage.getItem(getKey(key));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return { data, ts, age: Date.now() - ts };
  } catch {
    return null;
  }
}

export function setCache(key, data) {
  try {
    sessionStorage.setItem(getKey(key), JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // sessionStorage full — clear old entries
    clearOldCache();
  }
}

export function clearOldCache() {
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith(CACHE_PREFIX)) keys.push(k);
  }
  // Remove oldest half
  keys.sort().slice(0, Math.ceil(keys.length / 2)).forEach(k => sessionStorage.removeItem(k));
}

/**
 * Clear ALL pik_cache_ entries. Call from the logout flow so a different
 * user signing in on the same tab can't see the previous user's cached data.
 */
export function clearAllCache() {
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith(CACHE_PREFIX)) keys.push(k);
  }
  keys.forEach(k => sessionStorage.removeItem(k));
  try { sessionStorage.removeItem('pending_shared_contact'); } catch (e) { /* ignore */ }
}

/**
 * Load with stale-while-revalidate.
 * @param {string} key - Cache key
 * @param {() => Promise<any>} fetcher - Async function that returns fresh data
 * @param {(data: any) => void} onData - Called with data (cached first, then fresh)
 * @param {object} opts - { ttl, onError }
 */
export async function loadWithCache(key, fetcher, onData, opts = {}) {
  const { ttl = DEFAULT_TTL, onError } = opts;
  const cached = getCached(key);

  // Return cached data immediately if available
  if (cached) {
    onData(cached.data);

    // If cache is fresh enough, skip network
    if (cached.age < ttl) return cached.data;
  }

  // Fetch fresh data (in foreground if no cache, background if stale)
  try {
    const fresh = await fetcher();
    setCache(key, fresh);
    onData(fresh);
    return fresh;
  } catch (err) {
    if (!cached) {
      onError?.(err);
    }
    return cached?.data ?? null;
  }
}
